import type { ICarImageElement } from 'data/schema';
import { downloadBlob } from 'utils/ui/downloadImage';
import {
  buildUniqueEntryName,
  buildZipFilename,
  FETCH_TIMEOUT_MS,
  sanitizeFilename,
  withTimeout,
} from './downloadImagesAsZip';

export type TransparentDownloadProgress = (completed: number, total: number) => void;

function buildTransparentZipFilename(carName?: string): string {
  return buildZipFilename(carName).replace(/\.zip$/, '-transparent.zip');
}

async function fetchTransparentImage(image: ICarImageElement, signal: AbortSignal): Promise<Uint8Array> {
  const response = await fetch(image.source, { signal: withTimeout(signal, FETCH_TIMEOUT_MS) });
  if (!response.ok) {
    throw new Error(`Failed to fetch transparent image: ${response.status} ${response.statusText}`);
  }
  return new Uint8Array(await (await response.blob()).arrayBuffer());
}

export async function downloadTransparentImagesAsZip(
  images: ICarImageElement[],
  carName?: string,
  onProgress?: TransparentDownloadProgress
): Promise<void> {
  const imageItems = images.filter((item) => item.type === 'image');
  if (imageItems.length === 0) {
    throw new Error('No transparent images are available');
  }

  const seenNames = new Map<string, number>();
  const entries: Array<{ name: string; data: Uint8Array }> = [];

  for (let index = 0; index < imageItems.length; index += 1) {
    const image = imageItems[index];
    try {
      const data = await fetchTransparentImage(image, new AbortController().signal);
      const stem = carName ? `${carName} - ${image.title || 'image'}` : image.title || 'image';
      entries.push({
        name: buildUniqueEntryName(`${sanitizeFilename(stem)}.png`, seenNames),
        data,
      });
    } catch {
      // Individual vehicle angles may not exist for older models.
    } finally {
      onProgress?.(index + 1, imageItems.length);
    }
  }

  if (entries.length === 0) {
    throw new Error('No transparent images could be downloaded');
  }

  if (entries.length === 1) {
    const pngBuffer = entries[0].data.buffer as ArrayBuffer;
    downloadBlob(new Blob([pngBuffer], { type: 'image/png' }), entries[0].name);
    return;
  }

  const { zip } = await import('fflate');
  const zipInput: Record<string, [Uint8Array, { level: 0 }]> = {};
  entries.forEach(({ name, data }) => {
    zipInput[name] = [data, { level: 0 }];
  });

  const archive = await new Promise<Uint8Array<ArrayBuffer>>((resolve, reject) => {
    zip(zipInput, { level: 0 }, (error, data) => {
      if (error) reject(error);
      else resolve(data as Uint8Array<ArrayBuffer>);
    });
  });

  downloadBlob(new Blob([archive], { type: 'application/zip' }), buildTransparentZipFilename(carName));
}
